import type { PropsWithChildren } from 'react'
import { createContext, useEffect } from 'react'
import { shallow } from 'zustand/shallow'

import { AppError } from '../components/AppError'
import type { AppParams } from '../hooks/useAppParams/types'
import { useAppStoreSelector } from '../hooks/useAppStoreSelector'
import {
  getJourneyByIdQueryKey,
  useJourneyQuery
} from '../hooks/useJourneyQuery'
import { useLanguage } from '../hooks/useLanguage'
import { usePreview } from '../hooks/usePreview'
import { usePwaManifest } from '../hooks/usePwaManifest'
import { getJourneyById } from '../services/journey-service'
import { getQueryClient } from '../services/queryClient'
import { journeyHydrated } from '../store/actions/journeyHydrated'
import { PREVIEW_JOURNEY_ID } from '../store/actions/previewHydrate'
import { setAppError } from '../store/actions/setAppError'
import {
  selectAppStatus,
  selectLaunchableJourneyIdsSorted,
  selectSelectedJourneyId
} from '../store/selectors/config'
import { APP_ERROR, APP_STATUS } from '../store/types/enums'

export type AppManagerContextValue = {
  /**
   * App params coming directly from the URL or the provided Journey token.
   *
   * Tip: You should access values from store whenever possible because additional
   * logic might be applied elsewhere.
   */
  appParams: Readonly<AppParams>
}

export const AppManagerContext = createContext<
  AppManagerContextValue | undefined
>(undefined)

/**
 * Responsible for orchestrating the App by running the most foundamental actions.
 */
export const AppManager = (
  props: PropsWithChildren<{
    appParams: AppParams
  }>
) => {
  const appParams = props.appParams
  const appStatus = useAppStoreSelector(selectAppStatus)
  const selectedJourneyId = useAppStoreSelector(selectSelectedJourneyId)
  const linkedJourneyIds = useAppStoreSelector(
    selectLaunchableJourneyIdsSorted,
    shallow
  )

  useLanguage(appParams.lang)
  usePreview(appParams.preview)
  usePwaManifest()

  const rootQuery = useJourneyQuery(appParams.journeyId, {
    // is not preview and id exists
    enabled: !appParams.preview && !!appParams.journeyId
  })

  useEffect(() => {
    if (!appParams.preview && rootQuery?.data) {
      journeyHydrated({ response: rootQuery.data, diferentiator: 'root' })
    }
  }, [appParams.preview, rootQuery?.data])

  const selectedQuery = useJourneyQuery(selectedJourneyId, {
    enabled:
      // id exists
      !!selectedJourneyId &&
      // is not preview only
      selectedJourneyId !== PREVIEW_JOURNEY_ID &&
      // and is launchable
      linkedJourneyIds.includes(selectedJourneyId)
  })

  useEffect(() => {
    if (selectedQuery?.data) {
      journeyHydrated({
        response: selectedQuery.data,
        diferentiator: 'selectable'
      })
    }
  }, [selectedQuery?.data])

  useEffect(() => {
    if (
      appStatus !== APP_STATUS.FAILED &&
      !appParams.journeyId &&
      !appParams.preview
    ) {
      return void setAppError({
        error: APP_ERROR.JOURNEY_NOT_FOUND,
        reason: 'Journey details are missing and this is not a preview session.'
      })
    }
  }, [appStatus, appParams.journeyId, appParams.preview])

  useEffect(() => {
    for (const id of linkedJourneyIds) {
      getQueryClient().prefetchQuery({
        queryKey: getJourneyByIdQueryKey(id),
        queryFn: (context) => {
          return getJourneyById(id, context.signal)
        }
      })
    }
  }, [linkedJourneyIds])

  useEffect(() => {
    if (appStatus === APP_STATUS.FAILED) return

    if (rootQuery?.isError) {
      setAppError({
        error: APP_ERROR.JOURNEY_NOT_FOUND,
        reason: 'Journey was not found.'
      })
    } else if (selectedQuery?.isError) {
      setAppError({
        error: APP_ERROR.JOURNEY_NOT_FOUND,
        reason: 'Launchable Journey was not found.'
      })
    }
  }, [appStatus, rootQuery?.isError, selectedQuery?.isError])

  return (
    <AppManagerContext.Provider value={{ appParams }}>
      {/*
        In case of a fatal App error, renders an error page. Otherwise, renders children and let
        each component determine when its time to render (i.e. waiting for the ready state, showing UI skeletons).
      */}
      {appStatus === APP_STATUS.FAILED ? <AppError /> : props.children}
    </AppManagerContext.Provider>
  )
}
